import React, { useState, useEffect } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { GlobalDropdown } from './GlobalDropdown';
import { Toast } from './Toast';
import { useLocale } from '../context/DynamicLocaleProvider';
import { collection, addDoc } from 'firebase/firestore';
import styles from './ProjectModal.module.css';
import { db } from '../lib/firebase';
import { useCompanies } from '../hooks/useCompanies';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectAdded: () => void;
  /** When set (e.g. from company detail page), pre-selects this company and disables the dropdown */
  preselectedCompanyId?: string;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onProjectAdded,
  preselectedCompanyId
}) => {
  const { t } = useLocale();
  const { companies, loading: companiesLoading } = useCompanies(isOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    companyId: '',
    projectName: '',
    projectDescription: ''
  });

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        companyId: preselectedCompanyId || '',
        projectName: '',
        projectDescription: ''
      });
    } else {
      setFormData({
        companyId: '',
        projectName: '',
        projectDescription: ''
      });
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, preselectedCompanyId]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.companyId || !formData.projectName) {
      setError(t('project_modal_error_required_fields') || 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await addDoc(collection(db, 'projects'), {
        companyId: formData.companyId,
        projectName: formData.projectName,
        projectDescription: formData.projectDescription || ''
      });

      setSuccess(t('project_modal_success') || 'Project created successfully');
      
      // Wait a moment for the toast to be visible
      setTimeout(() => {
        onClose();
        setFormData({
          companyId: '',
          projectName: '',
          projectDescription: ''
        });
        setLoading(false);
        setSuccess(null);
        onProjectAdded();
      }, 1500);

    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to create project:', err);
      }
      setError(err instanceof Error ? err.message : t('project_modal_error_create_failed') || 'Failed to create project');
      setLoading(false);
    }
  };

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  if (!isOpen) return null;

  const companyOptions = companies.map(company => ({
    value: company.id,
    label: company.name
  }));

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>{t('project_modal_title') || 'Add project'}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <GlobalDropdown
            label={t('project_modal_company_label') || 'Company'}
            value={formData.companyId}
            onChange={(value) => handleInputChange('companyId', value)}
            options={companyOptions}
            placeholder={companiesLoading ? (t('project_modal_company_loading') || 'Loading companies...') : (t('project_modal_company_placeholder') || 'Select company')}
            required
            disabled={companiesLoading || !!preselectedCompanyId}
          />

          <GlobalInput
            label={t('project_modal_name_label') || 'Project Name'}
            value={formData.projectName}
            onChange={(e) => handleInputChange('projectName', e.target.value)}
            required
            placeholder={t('project_modal_name_placeholder') || 'Enter project name'}
          />

          <GlobalInput
            label={t('project_modal_description_label') || 'Description'}
            value={formData.projectDescription}
            onChange={(e) => handleInputChange('projectDescription', e.target.value)}
            placeholder={t('project_modal_description_placeholder') || 'Enter description (optional)'}
          />

          {/* Actions */}
          <div className={styles.actions}>
            <GlobalButton
              type="button"
              onClick={onClose}
              disabled={loading}
              variant="secondary"
            >
              {t('project_modal_cancel_button') || 'Cancel'}
            </GlobalButton>
            <GlobalButton
              type="submit"
              disabled={loading || companiesLoading || !formData.companyId || !formData.projectName.trim()}
              isLoading={loading}
              variant="primary"
            >
              {t('project_modal_create_button') || 'Create project'}
            </GlobalButton>
          </div>
        </form>

        {error && (
          <Toast
            message={error}
            type="error"
            isVisible={true}
            onClose={clearError}
          />
        )}

        {success && (
          <Toast
            message={success}
            type="success"
            isVisible={true}
            onClose={clearSuccess}
          />
        )}
      </div>
    </div>
  );
};

